import { BOT_VALUES, type ZodiacSign } from '@/api/consts'
import type { MatrixOfDestiny } from '@/api/services/numer'
import { AspectType, ObjectType, PlanetType } from '@/api/services/sweph-service/types'
import { ERROR_CODES } from '@/api/utils/dict'
import z from 'zod'

export const SynastryTypeSchema = z.enum(['astro', 'matrix'])

export const UserLocationSchema = z.object({
  id: z.number(),
  name_ru: z.string(),
  name_en: z.string(),
  name_ascii: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  country_code: z.string(),
  region_ru: z.string(),
  region_en: z.string(),
  population: z.number(),
  tz: z.string(),
})

export type UserLocation = z.infer<typeof UserLocationSchema>

export const SubscriptionStatusSchema = z.enum([
  'inactive',
  'active',
  'cancelled',
])

export const OfferIdSchema = z.enum([
  'micro',
  'one',
  'week',
  'full',
])

export type OfferId = z.infer<typeof OfferIdSchema>

export const UserPricingSchema = z.object({
  messages: z.record(OfferIdSchema, z.number()),
  synastries: z.record(OfferIdSchema, z.number()),
  matrixYear: z.record(OfferIdSchema, z.number()),
  amount: z.record(OfferIdSchema, z.number()),
  loyaltyAmount: z.record(OfferIdSchema, z.number()),
  loyaltyMessages: z.record(OfferIdSchema, z.number()),
})

export const UserDTOSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  pricingType: z.enum(['legacy', 'current']),
  hasAccess: z.boolean(),
  hasSubscription: z.boolean(),
  hasMatrixYearAccess: z.boolean(),
  subscriptionEndDate: z.date().nullable(),
  emailLetters: z.boolean().optional(),
  synastriesCount: z.number(),
  messagesCount: z.number(),
  hasPayment: z.boolean(),
  hasRegularOfferPayment: z.boolean(),
  hasLoyaltyDiscount: z.boolean(),
  pricing: UserPricingSchema,
})

export const ProfileDTOSchema = z.object({
  gender: z.enum(['male', 'female', 'androgynous']).nullable(),
  userName: z.string().nullable(),
  birthDate: z.string().nullable(),
  birthTime: z.string().nullable(),
  location: UserLocationSchema.nullable(),
})

export const SynastryDTOSchema = z.object({
  id: z.string(),
  userName: z.string(),
  birthDate: z.string(),
  birthTime: z.string().nullable(),
  location: UserLocationSchema.nullable(),
  createdAt: z.date(),
  type: SynastryTypeSchema,
  locked: z.boolean(),
})

export type UserDTO = z.infer<typeof UserDTOSchema>
export type ProfileDTO = z.infer<typeof ProfileDTOSchema>
export type SynastryDTO = z.infer<typeof SynastryDTOSchema>

export const DrawnCardSchema = z.object({
  index: z.number(),
  name: z.string(),
  suit: z.enum(['trump', 'cups', 'swords', 'wands', 'pentacles']),
  reversed: z.boolean(),
  position: z.string(),
})

export type DrawnCard = z.infer<typeof DrawnCardSchema>

export const TarotReadingSchema = z.object({
  spread: z.enum(['single', 'three', 'five']),
  cards: z.array(DrawnCardSchema),
})

export type TarotReading = z.infer<typeof TarotReadingSchema>

export const CtxSourceSchema = z.enum(['natal', 'matrix'])

export type CtxSource = z.infer<typeof CtxSourceSchema>

export const MessageCtxSchema = z.object({
  groupId: z.string().optional(),
  suggestions: z.array(z.string()).optional(),
  template: z.enum(['synastry', 'moon']).optional(),
})

export type MessageCtx = z.infer<typeof MessageCtxSchema>

export const MessageSchema = z.object({
  text: z.string(),
  skip: z.boolean().optional(),
  hidden: z.boolean().optional(),
  role: z.union([z.literal('user'), z.literal('model')]),
  action: z.union([
    z.literal('login'),
    z.literal('access'),
  ]).optional(),
  reading: TarotReadingSchema.optional(),
  ctx: MessageCtxSchema.optional(),
})

export const MessageListSchema = z.array(MessageSchema)

export type Message = z.infer<typeof MessageSchema>

export const BotSchema = z.enum(BOT_VALUES)

export type Bot = z.infer<typeof BotSchema>

export const MessagesSubmitParamsSchema = z.object({
  messages: z.array(MessageSchema).min(1),
  bot: BotSchema,
  tz: z.string(),
  ctx: MessageCtxSchema.optional(),
})

export type MessagesSubmitParams = z.infer<typeof MessagesSubmitParamsSchema>

export type MessagesSubmitResponse = {
  message: Message
  user: UserDTO
  profile: ProfileDTO
}

export type MessagesHistoryResponse = {
  messages: Record<Bot, Message[]>
}

export const MessagesHistoryDeleteParamsSchema = z.object({
  bot: BotSchema,
})

export type MessagesHistoryDeleteParams = z.infer<typeof MessagesHistoryDeleteParamsSchema>

export const FeedbackParamsSchema = z.object({
  feedback: z.string().trim().min(1).max(1000),
})

export type FeedbackParams = z.infer<typeof FeedbackParamsSchema>

export const ProfileParamsSchema = z.object({
  userName: z.string()
    .trim()
    .min(1, ERROR_CODES.nameRequired)
    .max(100, ERROR_CODES.nameNotValid)
    .regex(/^\S+(\s\S+)?$/, ERROR_CODES.nameNotValid)
    .optional(),
  birthDate: z.iso.date({ error: ERROR_CODES.dateNotValid }).optional(),
  birthTime: z.iso.time({ precision: 0, error: ERROR_CODES.timeNotValid }).optional(),
  location: UserLocationSchema.optional(),
})

export type ProfileParams = z.infer<typeof ProfileParamsSchema>

export const ProfilePatchParamsSchema = z.object({
  profile: ProfileParamsSchema,
  emailLetters: z.boolean().optional(),
})

export type ProfilePatchParams = z.infer<typeof ProfilePatchParamsSchema>

export type ProfilePatchResponse = {
  user: UserDTO
  profile: ProfileDTO
}

export const ProfileStateSchema = z.object({
  profile: ProfileParamsSchema,
  code: z.string().optional(),
  bot: z.string().optional(),
  from: z.string().optional(),
  emailLetters: z.boolean().optional(),
})

export type ProfileState = z.infer<typeof ProfileStateSchema>

export const SendOtpParamsSchema = z.object({
  email: z.email(ERROR_CODES.emailNotValid).trim(),
})

export type SendOtpParams = z.infer<typeof SendOtpParamsSchema>

export const VerifyOtpParamsSchema = z.object({
  email: z.email().trim(),
  code: z.string().trim().length(4, ERROR_CODES.otpNotValid),
  emailLetters: z.boolean().optional(),
  profile: ProfileParamsSchema.optional(),
})

export type VerifyOtpParams = z.infer<typeof VerifyOtpParamsSchema>

export const VerifyOtpResponseSchema = z.object({
  user: UserDTOSchema,
  profile: ProfileDTOSchema.optional(),
})

export type VerifyOtpResponse = z.infer<typeof VerifyOtpResponseSchema>

export const EmailLoginParamsSchema = z.object({
  email: z.email(ERROR_CODES.emailNotValid).trim(),
  emailLetters: z.boolean().optional(),
  profile: ProfileParamsSchema.optional(),
})

export type EmailLoginParams = z.infer<typeof EmailLoginParamsSchema>

export const EmailLoginResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('created'),
    user: UserDTOSchema,
    profile: ProfileDTOSchema.optional(),
  }),
  z.object({
    status: z.literal('otp'),
    code: z.string().optional(),
  }),
])

export type EmailLoginResponse = z.infer<typeof EmailLoginResponseSchema>

export const UserMeResponseSchema = z.object({
  user: UserDTOSchema,
  profile: ProfileDTOSchema.optional(),
})

export type UserMeResponse = z.infer<typeof UserMeResponseSchema>

export const OAuthLoginParamsSchema = z.object({
  profile: ProfileParamsSchema.optional(),
  bot: z.string().optional(),
  from: z.string().optional(),
  emailLetters: z.boolean().optional(),
})

export type OAuthLoginParams = z.infer<typeof OAuthLoginParamsSchema>

export const OAuthResponseSchema = z.object({
  redirectUrl: z.string(),
})

export type OAuthResponse = z.infer<typeof OAuthResponseSchema>

export const MessagesFreeSubmitParamsSchema = z.object({
  messages: z.array(MessageSchema).min(1),
  profile: ProfileParamsSchema,
  bot: BotSchema,
  tz: z.string(),
})

export type MessagesFreeSubmitParams = z.infer<typeof MessagesFreeSubmitParamsSchema>

export type MessagesFreeSubmitResponse = {
  message: Message
}

export const UserSummaryParamsSchema = z.object({
  profile: ProfileParamsSchema,
  from: z.string().nullable(),
})

export type UserSummaryParams = z.infer<typeof UserSummaryParamsSchema>

export type UserSummaryResponse = {
  text: string
}

export type AstroChart = {
  planets: Record<string, number[]>
  signs: Record<string, ZodiacSign>
  cusps: number[]
  aspects: {
    planet1: string
    planet2: string
    aspect: AspectType
    orb: number
  }[]
}

export type NatalSummary = {
  base?: string[]
  aspects?: string[]
  points?: string[]
  houses?: string[]
  sings?: string[]
  retro?: string[]
}

export const NatalParamsSchema = z.object({
  birthDate: z.iso.date({ error: ERROR_CODES.dateNotValid }),
  birthTime: z.iso.time({ precision: 0, error: ERROR_CODES.timeNotValid }).optional(),
  locationId: z.number().int().positive().optional(),
})

export type NatalParams = z.infer<typeof NatalParamsSchema>
export type NatalResponse = {
  chart: AstroChart
  // summary: NatalSummary
}

export const NatalSummaryFullSchema = z.object({
  base: z.record(z.string(), z.string()),
  planets: z.record(z.string(), z.string()),
  houses: z.record(z.string(), z.string()),
  points: z.record(z.string(), z.string()),
  retro: z.array(z.string()),
  aspects: z.array(z.string()),
})

export type NatalSummaryFull = z.infer<typeof NatalSummaryFullSchema>

export type NatalFullResponse = {
  fullSummary: NatalSummaryFull
}

export const MatrixSummaryFullSchema = z.object({
  base: z.object({
    dayArcana: z.string(),
    monthArcana: z.string(),
    yearArcana: z.string(),
    karmaArcana: z.string(),
    center: z.string(),
  }),
  lineage: z.object({
    father: z.string(),
    mother: z.string(),
    future: z.string(),
    past: z.string(),
    centerRod: z.string(),
  }),
  axes: z.object({
    left: z.string(),
    top: z.string(),
    right: z.string(),
    bottom: z.string(),
  }),
  purposes: z.object({
    heaven: z.string(),
    earth: z.string(),
    personal: z.string(),
    male: z.string(),
    female: z.string(),
    social: z.string(),
    spiritual: z.string(),
  }),
  wealth: z.object({
    moneyLine: z.string(),
    relationshipLine: z.string(),
    balance: z.string(),
  }),
  zones: z.object({
    material: z.string(),
    spiritual: z.string(),
    comfort: z.string(),
    money: z.string(),
  }),
})

export type MatrixSummaryFull = z.infer<typeof MatrixSummaryFullSchema>

export const MatrixParamsSchema = z.object({
  birthDate: z.iso.date({ error: ERROR_CODES.dateNotValid }),
})

export type MatrixParams = z.infer<typeof MatrixParamsSchema>

export type MatrixResponse = {
  matrix: MatrixOfDestiny
  summary: MatrixSummaryFull
}

export type MatrixFullResponse = {
  fullSummary: MatrixSummaryFull
}

export const DiscountTypeSchema = z.enum([
  'loyalty',
])

export type DiscountType = z.infer<typeof DiscountTypeSchema>

export const SubscriptionCreateParamsSchema = z.object({
  offerId: OfferIdSchema,
})

export type SubscriptionCreateParams = z.infer<typeof SubscriptionCreateParamsSchema>

export type SubscriptionCreateResponse = {
  confirmationUrl: string
}

export const SynastryParamsSchema = z.object({
  userName: z.string()
    .trim()
    .min(1, ERROR_CODES.nameRequired)
    .max(100, ERROR_CODES.nameNotValid)
    .regex(/^\S+(\s\S+)?$/, ERROR_CODES.nameNotValid),
  birthDate: z.iso.date({ error: ERROR_CODES.dateNotValid }),
  birthTime: z.iso.time({ precision: 0, error: ERROR_CODES.timeNotValid }).optional(),
  locationId: z.number().int().positive().optional(),
  type: SynastryTypeSchema,
})

export type SynastryParams = z.infer<typeof SynastryParamsSchema>
export type SynastryType = z.infer<typeof SynastryTypeSchema>

export type SynastryResponse = {
  id: string
}

export type SynastryListResponse = {
  synastries: SynastryDTO[]
}

export const SynastryStateSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('success'),
    text: z.string(),
  }),
  z.object({
    status: z.literal('loading'),
  }),
  z.object({
    status: z.literal('access'),
  }),
])

export const GuestDailyCardSchema = z.object({
  index: z.number(),
  name: z.string(),
  suit: z.enum(['trump', 'cups', 'swords', 'wands', 'pentacles']),
  reversed: z.boolean(),
})

export const GuestDailyAspectSchema = z.object({
  planet1: z.union([
    z.enum(PlanetType),
    z.enum(ObjectType),
  ]),
  planet2: z.union([
    z.enum(PlanetType),
    z.enum(ObjectType),
  ]),
  aspect: z.enum(AspectType),
  orb: z.number(),
  name: z.string(),
  description: z.string(),
})

export const GuestDailyMainSchema = z.object({
  title: z.string(),
  description: z.string(),
})

export const GuestDailyTodoSchema = z.object({
  do: z.array(z.string()),
  dont: z.array(z.string()),
})

export const DailyUserSpheresSchema = z.object({
  love: z.string(),
  friends: z.string(),
  work: z.string(),
  family: z.string(),
})

export const GuestDailyTarotSchema = z.object({
  message: z.string(),
})

export const DailyGuestParamsSchema = z.object({
  tz: z.string().trim().min(1),
})

export const DailyTransitParamsSchema = z.object({
  transitPlanet: z.nativeEnum(PlanetType),
  natalPlanet: z.nativeEnum(PlanetType),
  aspect: z.coerce.number().pipe(z.nativeEnum(AspectType)),
})

export const DailyTransitResponseSchema = z.object({
  title: z.string(),
  description: z.string(),
})

export const DailyUserTransitSchema = z.object({
  transitPlanet: z.nativeEnum(PlanetType),
  natalPlanet: z.nativeEnum(PlanetType),
  aspect: z.nativeEnum(AspectType),
  orb: z.number(),
})

export const DailyUserMainTransitSchema = DailyUserTransitSchema.extend({
  transitPlanetName: z.string(),
  natalPlanetName: z.string(),
  aspectName: z.string(),
  title: z.string(),
  description: z.string(),
})

export const DailyUserTransitTitleSchema = DailyUserTransitSchema.extend({
  transitPlanetName: z.string(),
  natalPlanetName: z.string(),
  aspectName: z.string(),
  title: z.string(),
  vibe: z.string(),
})

export const DailyUserResponseSchema = z.object({
  moonSign: z.string(),
  moonSignEmoji: z.string(),
  moonPhase: z.string(),
  moonPhaseEmoji: z.string(),
  sunSeasonEmoji: z.string(),
  mainMoonTransit: DailyUserMainTransitSchema.nullable(),
  mainFastTransit: DailyUserMainTransitSchema.nullable(),
  transits: z.array(DailyUserTransitTitleSchema),
  main: GuestDailyMainSchema,
  todo: GuestDailyTodoSchema,
  spheres: DailyUserSpheresSchema,
})

export const DailyCommonResponseSchema = z.object({
  moonSign: z.string(),
  moonSignEmoji: z.string(),
  moonPhase: z.string(),
  moonPhaseEmoji: z.string(),
  solarEclipse: z.boolean(),
  lunarEclipse: z.boolean(),
  mercuryRetro: z.boolean(),
  venusRetro: z.boolean(),
  marsRetro: z.boolean(),
  dailyCard: GuestDailyCardSchema,
  main: GuestDailyMainSchema,
  todo: GuestDailyTodoSchema,
  tarot: GuestDailyTarotSchema,
  transits: z.object({
    love: z.array(DailyUserTransitTitleSchema),
    family: z.array(DailyUserTransitTitleSchema),
    work: z.array(DailyUserTransitTitleSchema),
    friends: z.array(DailyUserTransitTitleSchema),
  }).nullable(),
  spheres: DailyUserSpheresSchema.nullable(),
})

export const GuestDailyResponseSchema = z.object({
  moonSign: z.string(),
  moonSignEmoji: z.string(),
  moonPhase: z.string(),
  moonPhaseEmoji: z.string(),
  sunSeason: z.string(),
  sunSeasonEmoji: z.string(),
  solarEclipse: z.boolean(),
  lunarEclipse: z.boolean(),
  mercuryRetro: z.boolean(),
  venusRetro: z.boolean(),
  marsRetro: z.boolean(),
  aspects: z.array(GuestDailyAspectSchema),
  dailyCard: GuestDailyCardSchema,
  main: GuestDailyMainSchema,
  todo: GuestDailyTodoSchema,
  tarot: GuestDailyTarotSchema,
})

export type GuestDailyCard = z.infer<typeof GuestDailyCardSchema>
export type GuestDailyAspect = z.infer<typeof GuestDailyAspectSchema>
export type GuestDailyMain = z.infer<typeof GuestDailyMainSchema>
export type GuestDailyTodo = z.infer<typeof GuestDailyTodoSchema>
export type DailyUserSpheres = z.infer<typeof DailyUserSpheresSchema>
export type GuestDailyTarot = z.infer<typeof GuestDailyTarotSchema>
export type DailyGuestParams = z.infer<typeof DailyGuestParamsSchema>
export type DailyTransitParams = z.infer<typeof DailyTransitParamsSchema>
export type DailyTransitResponse = z.infer<typeof DailyTransitResponseSchema>
export type DailyCommonResponse = z.infer<typeof DailyCommonResponseSchema>
export type DailyUserResponse = z.infer<typeof DailyUserResponseSchema>
export type GuestDailyResponse = z.infer<typeof GuestDailyResponseSchema>
